import { Icon } from '@/components/Icon';
import { useEffect, useId, useMemo, useRef, useState, type CSSProperties } from 'react';

// 검색해서 고르는 칸 — 이미 받아 둔 목록(수십 건)을 글자로 거른다. 서버 검색이 필요한 큰 목록은
// EntityPicker 를 쓴다. 2026-10-19 오너 요청("고객사 검색도 가능하게") — 일정 추가 창의 고객사 칸.
//
// · 칸을 누르면 전체 목록이 펼쳐지고, 치면 이름(과 search 글자)에 들어 있는 것만 남는다(띄어쓰기 ·
//   대소문자 무시).
// · ↑ ↓ 로 옮기고 Enter 로 고른다. Esc · 바깥 누르기는 닫고 고른 이름으로 되돌린다.
// · 목록은 칸 아래에 그대로 펼친다(창 안에서 잘리지 않게 — 떠 있는 상자가 아니다).

export interface SearchSelectOption {
  value: string;
  label: string;
  /** 이름 옆에 흐리게 — 예) '지금 10/6' */
  hint?: string;
  /** 이름 말고도 검색에 걸릴 글자 — 예) 계약 번호 */
  search?: string;
}

interface Props {
  options: SearchSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  loading?: boolean;
  /** 목록을 못 받았을 때 — '맞는 항목이 없습니다'와 헷갈리지 않게 따로 알린다. */
  error?: boolean;
  emptyMessage?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}

const norm = (s: string) => s.toLowerCase().replace(/\s+/g, '');

export function SearchSelect({
  options,
  value,
  onChange,
  placeholder,
  loading = false,
  error = false,
  emptyMessage = '맞는 항목이 없습니다',
  'aria-label': ariaLabel,
  'data-testid': testId = 'search-select',
}: Props) {
  const picked = options.find((o) => o.value === value) ?? null;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const filtered = useMemo(() => {
    const q = norm(query);
    if (!q) return options;
    return options.filter((o) => norm(`${o.label} ${o.search ?? ''}`).includes(q));
  }, [options, query]);

  // 바깥을 누르면 닫는다.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  // 키보드로 옮긴 항목이 보이게.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.children[active] as HTMLElement | undefined;
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  function openList() {
    setQuery('');
    setActive(
      Math.max(
        0,
        options.findIndex((o) => o.value === value),
      ),
    );
    setOpen(true);
  }

  function pick(o: SearchSelectOption) {
    onChange(o.value);
    setOpen(false);
    setQuery('');
  }

  return (
    <div ref={boxRef} style={{ position: 'relative' }} data-testid={testId}>
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        value={open ? query : (picked?.label ?? '')}
        placeholder={open && picked ? picked.label : loading ? '불러오는 중…' : placeholder}
        onFocus={openList}
        onClick={() => !open && openList()}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!open) openList();
            else setActive((i) => Math.min(i + 1, filtered.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
          } else if (e.key === 'Enter') {
            if (open && filtered[active]) {
              e.preventDefault();
              pick(filtered[active]);
            }
          } else if (e.key === 'Escape' && open) {
            // 창(Modal)까지 닫히지 않게 여기서 멈춘다.
            e.stopPropagation();
            setOpen(false);
          }
        }}
        style={inputSt}
        data-testid={`${testId}-input`}
      />
      <span aria-hidden style={caret}>
        <Icon name={open ? 'chev-up' : 'chev-down'} size={14} aria-hidden />
      </span>
      {open && (
        <div id={listId} role="listbox" ref={listRef} style={listSt} data-testid={`${testId}-list`}>
          {filtered.length === 0 ? (
            <div style={error ? { ...emptySt, color: 'var(--fg-danger)' } : emptySt}>
              {loading
                ? '불러오는 중…'
                : error
                  ? '목록을 불러오지 못했습니다. 잠시 뒤 다시 열어 주세요.'
                  : query.trim()
                    ? emptyMessage
                    : '고를 항목이 없습니다'}
            </div>
          ) : (
            filtered.map((o, i) => (
              <div
                key={o.value}
                role="option"
                aria-selected={o.value === value}
                // mousedown — 입력칸의 blur 보다 먼저 고른다.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(o);
                }}
                onMouseEnter={() => setActive(i)}
                style={{
                  ...optionSt,
                  background:
                    i === active
                      ? 'var(--bg-muted)'
                      : o.value === value
                        ? 'var(--brand-subtle)'
                        : 'transparent',
                  fontWeight: o.value === value ? 600 : 400,
                }}
                data-testid={`${testId}-option-${o.value}`}
              >
                <span>{o.label}</span>
                {o.hint && <span style={hintSt}>{o.hint}</span>}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const inputSt: CSSProperties = {
  width: '100%',
  height: 36,
  padding: '6px 28px 6px 10px',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
  boxSizing: 'border-box',
};
const caret: CSSProperties = {
  position: 'absolute',
  right: 10,
  top: 11,
  display: 'inline-flex',
  color: 'var(--fg-muted)',
  pointerEvents: 'none',
};
const listSt: CSSProperties = {
  marginTop: 4,
  maxHeight: 220,
  overflowY: 'auto',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
};
const optionSt: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 8,
  padding: '7px 10px',
  fontSize: 13,
  cursor: 'pointer',
};
const hintSt: CSSProperties = { color: 'var(--fg-muted)', fontSize: 12, whiteSpace: 'nowrap' };
const emptySt: CSSProperties = { padding: '8px 10px', fontSize: 12, color: 'var(--fg-muted)' };
